import { Group } from "@fairfleet/geotab";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import useTheme from "@mui/material/styles/useTheme";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import GroupEditPanel from "./components/GroupEditPanel";
import GroupTreeColumns from "./components/GroupTreeColumns";
import {
  buildChildColumns,
  canModifyGroup,
  findCompanyGroup,
  normalizePathIds,
  pathOrRoot,
  resolveParentId,
} from "./utils/groupTree";
import { normalizeGroupName } from "./utils/normalizeGroupName";
import { GroupsTruncatedAlert } from "@/components/GroupsTruncatedAlert";
import { useGroups } from "@/hooks/groups/useGroups";
import { buildGroupIndex } from "@/utilities/groupIndex";

const BUILT_IN_ORDER = [
  "Asset Information",
  "Driver Activity",
  "Asset Purpose",
  "Asset Type",
  "Powertrain and Fuel Type",
  "Container",
  "Equipment",
  "Trailer",
  "Vehicle",
];

export default function GroupsPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const { data: allGroups = [], isLoading, isFetching, isError, refetch } = useGroups();

  const [selectedPathIds, setSelectedPathIds] = useState<string[]>([]);

  const collator = useMemo(
    () =>
      new Intl.Collator("de", {
        sensitivity: "base",
        numeric: true,
      }),
    [],
  );

  const builtInRank = useMemo(() => {
    return new Map(BUILT_IN_ORDER.map((name, index) => [normalizeGroupName(name), index]));
  }, []);

  const { byId: groupById, parentById: parentByChildId } = useMemo(
    () => buildGroupIndex(allGroups),
    [allGroups],
  );

  const companyGroup = useMemo(() => findCompanyGroup(allGroups), [allGroups]);

  const normalizedPathIds = useMemo(
    () => normalizePathIds(selectedPathIds, companyGroup?.id, groupById),
    [companyGroup, groupById, selectedPathIds],
  );

  const pathGroups = useMemo(
    () => normalizedPathIds.map(id => groupById.get(id)).filter((group): group is Group => !!group),
    [groupById, normalizedPathIds],
  );

  const childColumns = useMemo(
    () => buildChildColumns(pathGroups, groupById, builtInRank, collator.compare),
    [builtInRank, collator, groupById, pathGroups],
  );

  const onSelectGroup = (depth: number, groupId: string) => {
    setSelectedPathIds(prev => [...pathOrRoot(prev, companyGroup?.id).slice(0, depth + 1), groupId]);
  };

  const selectedGroup = pathGroups[pathGroups.length - 1] ?? companyGroup;
  const selectedGroupParentId = resolveParentId(selectedGroup, parentByChildId);

  if (isLoading) {
    return (
      <Box sx={{ p: 4, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2">{t("groupsPage.loading")}</Typography>
        </Stack>
      </Box>
    );
  }

  if (isError && !companyGroup) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void refetch()}>
              {t("groupsPage.retryAction")}
            </Button>
          }
        >
          {t("groupsPage.loadError")}
        </Alert>
      </Box>
    );
  }

  if (!companyGroup || !selectedGroup) {
    return (
      <Box sx={{ p: 4 }}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t("groupsPage.noData")}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <GroupsTruncatedAlert sx={{ mb: 2 }} />
      {isError ? (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => void refetch()}>
              {t("groupsPage.retryAction")}
            </Button>
          }
        >
          {t("groupsPage.refreshError")}
        </Alert>
      ) : null}
      {isFetching ? <LinearProgress sx={{ mb: 2 }} /> : null}
      <Stack direction={isMobile ? "column" : "row"} spacing={2} alignItems="stretch">
        <GroupEditPanel
          selectedGroup={selectedGroup}
          parentId={selectedGroupParentId}
          parentName={selectedGroupParentId ? (groupById.get(selectedGroupParentId)?.name ?? "") : ""}
          canModify={canModifyGroup(selectedGroup, companyGroup.id)}
          isMobile={isMobile}
          onChildAdded={newGroupId =>
            setSelectedPathIds(prev => [...pathOrRoot(prev, companyGroup.id), newGroupId])
          }
          onDeleted={() => setSelectedPathIds(prev => prev.slice(0, -1))}
        />
        <GroupTreeColumns
          companyGroup={companyGroup}
          columns={childColumns}
          pathGroups={pathGroups}
          selectedGroupId={selectedGroup.id}
          isMobile={isMobile}
          onSelectCompany={() => setSelectedPathIds([companyGroup.id])}
          onSelect={onSelectGroup}
        />
      </Stack>
    </Box>
  );
}
